import styled from '@emotion/styled';
import { ifProp, switchProp } from 'styled-tools';
import { breakpoints } from '~/theme';
import { Text } from '$shared/components/Text';
import { Link } from '~/shared/components/Link';
import { AspectRatio } from '~/shared/components';
import { ellipsis, scrollbarThin } from '~/shared/utils/styled';
import { getClampFullRange } from '~/theme/utils/getClampValue';

export const StyledProductCardLink = styled(Link)(({ theme }) => ({
    fontFamily: `${theme.fonts.body}`,
    color: `${theme.colors.dark}`,
    display: 'block',
}));

export const StyledImagesContainer = styled.div({
    position: 'relative',
});

export const StyledDots = styled.div(({ theme }) => ({
    display: 'flex',
    justifyContent: 'center',
    gap: theme.spaces[1],
    position: 'absolute',
    bottom: theme.spaces[3],
    left: '0',
    right: '0',
    zIndex: theme.zIndices[1],
    pointerEvents: 'none',
}));

export const StyledDot = styled.div<{ isActive: boolean }>(({ theme, isActive }) => ({
    width: '5px',
    height: '5px',
    borderRadius: '50%',
    backgroundColor: isActive ? theme.colors.dark : theme.colors.light50,
}));

export const StyledDetails = styled.div<{ insideScroll?: boolean }>(
    ({ theme: { spaces, spacesRaw } }) => ({
        padding: `${getClampFullRange(spacesRaw[2], spacesRaw[3])} 0`,
        textDecoration: 'none',
        [breakpoints.md]: {
            padding: `${spaces[2]} 0 ${spaces[3]}`,
        },
    }),
    switchProp('alignment', {
        true: {
            color: '$grey50',
        },
    }),
    ifProp('insideScroll', {
        marginBottom: 0,
        paddingBottom: 0,
        flex: '1',
        minHeight: '145px',
        [breakpoints.sm]: {
            minHeight: '130px',
        }
    })
);

export const StyledColorsText = styled(Text)(({ theme }) => ({
    color: `${theme.colors.dark70}`,
    lineHeight: theme.lineHeights.compact,
    flex: '1 0 auto',
    whiteSpace: 'nowrap',
    fontWeight: theme.fontWeights.medium,
    marginTop: theme.spaces[2],
    [breakpoints.md]: {
        marginTop: 0,
        color: `${theme.colors.dark50}`,
        fontWeight: theme.fontWeights.regular,
    },
}));

export const StyledName = styled.div(({ theme }) => ({
    color: theme.colors.dark,
    width: '100%',
    alignItems: 'center',
    display: 'flex',
    justifyContent: 'space-between',
    position: 'relative',
    gap: theme.spaces[2],
    marginBottom: theme.spaces[1],
    [breakpoints.md]: {
        marginBottom: theme.spaces[2],
    },
}));

export const StyledTitlesWrapper = styled.div({
    flex: '1 1 auto',
    minWidth: 0,
});

export const StyledTitle = styled(Text)(({ theme }) => ({
    width: '100%',
    ...ellipsis,
    fontWeight: theme.text.weights.bold,

    [breakpoints.md]: {
        fontWeight: theme.fontWeights.regular,
    },
}));

export const StyledSubtitle = styled(Text)(({ theme }) => ({
    color: theme.colors.dark70,
    fontWeight: theme.fontWeights.medium,
    marginTop: 2,
    ...ellipsis,

    [breakpoints.md]: {
        marginTop: 0,
        color: theme.colors.dark50,
        fontWeight: theme.fontWeights.regular,
        fontSize: theme.text.variants.bodySm,
        lineHeight: theme.lineHeights.base,
    },
}));

export const StyledBrand = styled.div(({ theme }) => ({
    fontSize: theme.fontSizes['2xs'],
    lineHeight: '14px',
    fontWeight: theme.fontWeights.regular,
    marginBottom: theme.spaces[1],
    color: theme.colors.light50,
    letterSpacing: '0.5px',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    width: '100%',
    textDecoration: 'none',
}));

export const StyledAttributesWrapper = styled.div(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    gap: theme.spaces[2],
}));

export const StyledAttributes = styled.div(({ theme }) => ({
    flex: '1 1 auto',
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spaces[1],
}));

export const StyledHoverIndicatorWrapper = styled.div(({ theme }) => ({
    display: 'none',
    flex: '0 1 24px',
    width: 24,
    height: 24,

    [breakpoints.md]: {
        display: 'initial',
    },

    ['svg']: {
        width: 'inherit',
        height: 'inherit',
        opacity: 0,
        transition: `opacity ${theme.animations.durations.moderate01}s ${theme.animations.easings.exit.productive}`,

        [breakpoints.md]: {
            'a:hover &': {
                transitionDuration: theme.animations.getDuration('slow02'),
                transitionTimingFunction: theme.animations.getEasing('entrance', 'productive'),
                opacity: 1,
            },
        },
    },
}));

export const StyledAspectRatio = styled(AspectRatio)({
    width: '100%',
    display: 'inline-block',
    scrollSnapAlign: 'center',
    [breakpoints.lg]: {
        display: 'block',
    },
});

export const StyledPrimaryImageWrapper = styled.div({
    position: 'relative',
    display: 'inline-block',
    width: '100%',
    scrollSnapAlign: 'start',
});

export const StyledSecondaryImageWrapper = styled.div<{
    isImageLoaded: boolean;
    insideScroll: boolean;
}>(({ theme, isImageLoaded, insideScroll }) => ({
    display: insideScroll ? 'none' : 'inline-block',
    width: '100%',
    scrollSnapAlign: 'start',
    [breakpoints.md]: {
        display: 'initial',
        position: 'absolute',
        inset: '0 0 auto',
        opacity: 0,
        zIndex: theme.zIndices[1],

        'a &:hover': {
            opacity: isImageLoaded ? 1 : 0,
        },
    },
}));

export const StyledSwipableContainer = styled.div(({ theme }) => ({
    position: 'relative',
    width: '100%',
    overflowX: 'auto',
    overflowY: 'hidden',
    whiteSpace: 'nowrap',
    scrollbarWidth: 'none',
    scrollSnapType: 'x mandatory',
    aspectRatio: `${theme.aspectRatios.productImageSingle}`,
    ...scrollbarThin,
}));

export const StyledProductTagsContainer = styled.div<{ position: 'top' | 'bottom' }>(
    {
        '&:empty': {
            display: 'none',
        },
    },
    switchProp('position', {
        top: ({ theme }) => ({
            position: 'absolute',
            top: theme.spaces[2],
            left: 0,
            right: 0,
            zIndex: theme.zIndices[1],
            [breakpoints.md]: {
                top: theme.spaces[3],
            },
        }),
    })
);

export const StyledProductCardSkeleton = styled.div<{ fillHeight?: boolean }>(
    ifProp('fillHeight', () => ({
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
    }))
);
